import * as React from 'react'
import { cn } from '../lib/utils'

export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  /** Additional wrapper for content with max-width container */
  container?: boolean
}

/**
 * Section wrapper component with consistent padding and spacing.
 * Use this to wrap major page sections for uniform layout.
 */
const Section = React.forwardRef<HTMLElement, SectionProps>(
  ({ className, container = true, children, ...props }, ref) => {
    return (
      <section ref={ref} className={cn('py-16 md:py-24', className)} {...props}>
        {container ? (
          <div className="container mx-auto px-4 md:px-6">{children}</div>
        ) : (
          children
        )}
      </section>
    )
  }
)
Section.displayName = 'Section'

export interface SectionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Section title */
  title: string
  /** Optional subtitle/description */
  subtitle?: string
  /** Center align the header */
  centered?: boolean
}

/**
 * Section header with title and optional subtitle.
 * Common pattern for introducing major page sections.
 */
const SectionHeader = React.forwardRef<HTMLDivElement, SectionHeaderProps>(
  ({ className, title, subtitle, centered = true, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('mb-12 space-y-4', centered && 'text-center', className)}
        {...props}
      >
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
        {subtitle && (
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{subtitle}</p>
        )}
      </div>
    )
  }
)
SectionHeader.displayName = 'SectionHeader'

export { Section, SectionHeader }
